import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@core-ui/Components/ui/badge';
import { FileText, X, Check, AlertTriangle, XCircle } from 'lucide-react';

import type { CsvValidationStats } from '../../utils/validateCsvContacts';

interface CsvPreviewStatsProps {
  stats: CsvValidationStats;
  fileName?: string;
  fileSize?: number;
  onRemoveFile?: () => void;
  variant?: 'full' | 'compact';
}

function formatFileSize (bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  return `${(bytes / 1024).toFixed(1)} KB`;
}

export default function CsvPreviewStats ({
  stats,
  fileName,
  fileSize,
  onRemoveFile,
  variant = 'full',
}: CsvPreviewStatsProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="outline" className="gap-1 border-teal-500 text-teal-600 dark:text-teal-400">
          {stats.total} {t('contacts.csvPreview.total', 'contactos')}
        </Badge>
        <Badge variant="outline" className="gap-1 border-emerald-500 text-emerald-600 dark:text-emerald-400">
          <Check className="h-3 w-3" />
          {stats.valid} {t('contacts.csvPreview.valid', 'validos')}
        </Badge>
        {stats.warnings > 0 && (
          <Badge variant="outline" className="gap-1 border-amber-500 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-3 w-3" />
            {stats.warnings} {t('contacts.csvPreview.warnings', 'advertencias')}
          </Badge>
        )}
        {stats.critical > 0 && (
          <Badge variant="outline" className="gap-1 border-red-500 text-red-600 dark:text-red-400">
            <XCircle className="h-3 w-3" />
            {stats.critical} {t('contacts.csvPreview.critical', 'criticos')}
          </Badge>
        )}
      </div>

      {variant === 'full' && fileName !== undefined && (
        <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-1.5 text-sm text-muted-foreground">
          <FileText className="h-4 w-4 flex-shrink-0" />
          <span className="truncate max-w-[180px]">{fileName}</span>
          {fileSize !== undefined && (
            <span className="text-xs text-muted-foreground/70">{formatFileSize(fileSize)}</span>
          )}
          {onRemoveFile !== undefined && (
            <button
              type="button"
              onClick={onRemoveFile}
              className="ml-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
